import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, MapPin, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { AssignPlotToMemberModal } from '@/components/plots/assign-plot-to-member-modal'
import { useAssignPlotToMember, useReleasePlotById } from '@/hooks/use-plots'
import { getApiErrorMessage } from '@/lib/api'
import type { MemberPlotSummary } from '@riverpark/types'

interface ProfilePlotsPanelProps {
  plots: MemberPlotSummary[]
  readOnly: boolean
  memberId?: string
  memberName?: string
  /** When set, read-only plot cards link to the member plot detail page. */
  plotHref?: (plot: MemberPlotSummary) => string
  /** Base path for the staff "View plot" link, e.g. /operations/plots */
  plotsBasePath?: string
}

export function ProfilePlotsPanel({
  plots,
  readOnly,
  memberId,
  memberName,
  plotHref,
  plotsBasePath,
}: ProfilePlotsPanelProps) {
  const [assignOpen, setAssignOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const assignPlot = useAssignPlotToMember(memberId ?? '')
  const releasePlot = useReleasePlotById()

  const canManage = !readOnly && !!memberId && !!memberName

  async function handleAssign(plotId: string, transferNote?: string, overrideDuesReason?: string) {
    setActionError(null)
    const result = await assignPlot.mutateAsync({ plotId, transferNote, overrideDuesReason })
    if (result.data.pendingApproval) {
      alert('Assignment requested — sent for committee/admin approval.')
    }
  }

  async function handleRelease(plot: MemberPlotSummary, overrideDuesReason?: string) {
    if (!overrideDuesReason && !confirm(`Release plot ${plot.newNumber} from this member?`)) return
    setActionError(null)
    try {
      const result = await releasePlot.mutateAsync({ plotId: plot.id, overrideDuesReason })
      if (result.data.pendingApproval) {
        alert('Release requested — sent for committee/admin approval.')
      }
    } catch (err) {
      const message = getApiErrorMessage(err, 'Could not release plot')
      if (!overrideDuesReason && message.includes('outstanding balance')) {
        const reason = prompt(
          `${message}\n\nEnter a reason to override and release anyway (leave blank to cancel):`,
        )
        if (reason?.trim()) {
          await handleRelease(plot, reason.trim())
        }
        return
      }
      setActionError(message)
    }
  }

  if (readOnly) {
    if (plots.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex size-14 items-center justify-center rounded-full border border-border-2 bg-cream-2">
              <MapPin className="size-6 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <p className="font-serif text-lg text-ink">No plots assigned</p>
            <p className="mt-2 max-w-sm font-sans text-sm text-muted-foreground">
              Plot numbers are assigned by the estate office. Contact the welfare society if you
              believe this is incorrect.
            </p>
          </CardContent>
        </Card>
      )
    }

    return (
      <div className="grid gap-4 sm:grid-cols-2">
        {plots.map((plot) => {
          const href = plotHref?.(plot)
          const cardClassName = href
            ? 'overflow-hidden transition-colors hover:border-green/40 hover:shadow-sm'
            : 'overflow-hidden'
          const cardBody = (
            <>
              <div className="h-1 bg-green" aria-hidden />
              <CardContent className="p-5">
                <p className="font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Plot number
                </p>
                <p className="mt-1 font-serif text-3xl font-medium tracking-tight text-ink">
                  {plot.newNumber}
                </p>
                <p className="mt-2 font-sans text-sm text-muted-foreground">
                  Legacy ref. <span className="text-ink">{plot.oldNumber}</span>
                </p>
              </CardContent>
            </>
          )

          if (href) {
            return (
              <Link
                key={plot.id}
                to={href}
                className="block rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Card className={cardClassName}>{cardBody}</Card>
              </Link>
            )
          }

          return (
            <Card key={plot.id} className={cardClassName}>
              {cardBody}
            </Card>
          )
        })}
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="font-serif text-lg text-ink">Assigned plots</h3>
            <p className="mt-1 font-sans text-sm text-muted-foreground">
              Assign from here or open the plot page for full ownership history.
            </p>
          </div>
          {canManage && (
            <Button
              type="button"
              size="sm"
              className="shrink-0"
              onClick={() => setAssignOpen(true)}
            >
              <Plus className="size-4" aria-hidden />
              Assign plot
            </Button>
          )}
        </div>

        {actionError && (
          <p className="font-sans text-sm text-destructive" role="alert">
            {actionError}
          </p>
        )}

        {plots.length === 0 ? (
          <div className="flex flex-col items-center rounded border border-dashed border-border-2 py-12 text-center">
            <MapPin className="mb-3 size-8 text-muted-foreground" strokeWidth={1.5} />
            <p className="font-serif text-lg text-ink">No plots assigned</p>
            <p className="mt-1 max-w-sm font-sans text-sm text-muted-foreground">
              Link this member to a plot from the estate register.
            </p>
            {canManage && (
              <Button type="button" className="mt-4" size="sm" onClick={() => setAssignOpen(true)}>
                Assign plot
              </Button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-border-2 rounded border border-border-2">
            {plots.map((plot) => (
              <li
                key={plot.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div>
                  <p className="font-serif text-lg text-ink">Plot {plot.newNumber}</p>
                  <p className="font-sans text-xs text-muted-foreground">
                    Legacy ref. {plot.oldNumber}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {plotsBasePath && (
                    <Link
                      to={`${plotsBasePath}/${plot.id}`}
                      className="inline-flex items-center gap-1.5 font-sans text-sm text-primary hover:text-green-lt"
                    >
                      View plot
                      <ExternalLink className="size-3.5" aria-hidden />
                    </Link>
                  )}
                  {canManage && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={releasePlot.isPending}
                      onClick={() => void handleRelease(plot)}
                    >
                      Release
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {canManage && (
        <AssignPlotToMemberModal
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          memberName={memberName}
          onAssign={handleAssign}
          loading={assignPlot.isPending}
        />
      )}
    </>
  )
}
